import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Landmark, Plus, Edit, Trash2, RefreshCw, Wallet, Building2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);

const emptyAccount = {
  name: "",
  bank_name: "",
  agency: "",
  account_number: "",
  account_type: "corrente",
  initial_balance: 0,
  notes: "",
};

const ACCOUNT_TYPE_LABELS = {
  corrente: "Conta Corrente",
  poupanca: "Poupança",
  caixa: "Caixa (Espécie)",
  outra: "Outra",
};

export default function BankAccountsTab({ companyId, onDataChange }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, data: { ...emptyAccount } });

  const fetchAll = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/companies/${companyId}/bank-accounts`);
      setAccounts(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar contas: " + (e.response?.data?.detail || ""));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, [companyId]);

  const save = async () => {
    if (!dlg.data.name?.trim()) return toast.error("Nome da conta obrigatório");
    try {
      const payload = { ...dlg.data, initial_balance: parseFloat(dlg.data.initial_balance) || 0 };
      if (dlg.data.id) {
        await axios.put(`${API}/companies/${companyId}/bank-accounts/${dlg.data.id}`, payload);
      } else {
        await axios.post(`${API}/companies/${companyId}/bank-accounts`, payload);
      }
      toast.success("Conta salva");
      setDlg({ open: false, data: { ...emptyAccount } });
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const del = async (acc) => {
    if (!window.confirm(`Excluir conta "${acc.name}"?`)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/bank-accounts/${acc.id}`);
      toast.success("Conta excluída");
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const recompute = async () => {
    try {
      const r = await axios.post(`${API}/companies/${companyId}/bank-accounts/recompute-balances`);
      toast.success(r.data?.message || "Saldos recalculados");
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const migrateLegacy = async () => {
    if (!window.confirm("Vincular todas as movimentações antigas (sem conta) à conta 'Caixa Geral'? Esta operação recalcula o saldo dela.")) return;
    try {
      const r = await axios.post(`${API}/companies/${companyId}/bank-accounts/migrate-legacy`);
      toast.success(`${r.data.transactions_migrated} transações + ${r.data.accounts_payable_migrated} contas vinculadas à Caixa Geral`);
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const totalGeral = accounts.reduce((s, a) => s + (parseFloat(a.current_balance) || 0), 0);

  return (
    <div className="space-y-4 bank-accounts-tab" data-testid="bank-accounts-tab">
      <style>{`
        .bank-accounts-tab, .bank-accounts-tab * {
          color: #000000 !important;
        }
        .bank-accounts-tab input, .bank-accounts-tab textarea, .bank-accounts-tab select,
        .bank-accounts-tab [role="combobox"] {
          color: #000000 !important;
        }
        .bank-accounts-tab input::placeholder,
        .bank-accounts-tab textarea::placeholder {
          color: #4b5563 !important;
          opacity: 1 !important;
        }
        .bank-accounts-tab label { font-weight: 700 !important; }
        .bank-accounts-tab .balance-positive { color: #059669 !important; }
        .bank-accounts-tab .balance-negative { color: #dc2626 !important; }
        .bank-accounts-tab .balance-total { color: #1d4ed8 !important; }
      `}</style>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <Landmark className="h-6 w-6 text-blue-600" /> Saldo de Contas Bancárias
        </h2>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={recompute} data-testid="bank-recompute-btn"><RefreshCw className="h-4 w-4 mr-1" /> Recalcular Saldos</Button>
          <Button variant="outline" onClick={migrateLegacy} data-testid="bank-migrate-btn"><Wallet className="h-4 w-4 mr-1" /> Vincular Antigas ao Caixa Geral</Button>
          <Button className="bg-blue-500 hover:bg-blue-600" onClick={() => setDlg({ open: true, data: { ...emptyAccount } })} data-testid="bank-new-btn"><Plus className="h-4 w-4 mr-1" /> Nova Conta</Button>
        </div>
      </div>

      {/* Card resumo total */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-bold">Saldo Total (soma de todas as contas)</p>
            <p className="text-2xl md:text-3xl font-extrabold balance-total" data-testid="bank-total-balance">{fmt(totalGeral)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold">{accounts.length} conta(s) cadastrada(s)</p>
          </div>
        </CardContent>
      </Card>

      {/* Lista de contas */}
      {loading ? (
        <p className="text-center py-4 font-bold">Carregando...</p>
      ) : accounts.length === 0 ? (
        <Card><CardContent className="p-6 text-center font-bold">Nenhuma conta cadastrada</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <Card key={a.id} className={a.is_default ? "border-blue-300" : ""}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 truncate font-bold">
                    <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
                    <span className="truncate">{a.name}</span>
                  </span>
                  {a.is_default && <Badge variant="outline" className="text-[10px] shrink-0 font-bold">Padrão</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div>
                  <p className="text-xs font-bold">Saldo Atual</p>
                  <p className={`text-xl font-extrabold ${a.current_balance >= 0 ? "balance-positive" : "balance-negative"}`} data-testid={`bank-balance-${a.id}`}>
                    {fmt(a.current_balance)}
                  </p>
                </div>
                <div className="text-xs space-y-0.5 font-semibold">
                  <p><strong>Tipo:</strong> {ACCOUNT_TYPE_LABELS[a.account_type] || a.account_type}</p>
                  {a.bank_name && <p><strong>Banco:</strong> {a.bank_name}</p>}
                  {a.agency && <p><strong>Agência:</strong> {a.agency}</p>}
                  {a.account_number && <p><strong>Conta:</strong> {a.account_number}</p>}
                  <p><strong>Saldo Inicial:</strong> {fmt(a.initial_balance)}</p>
                </div>
                <div className="flex gap-1 pt-2">
                  <Button size="sm" variant="outline" onClick={() => setDlg({ open: true, data: {
                    ...a,
                    bank_name: a.bank_name || "",
                    agency: a.agency || "",
                    account_number: a.account_number || "",
                    notes: a.notes || "",
                    initial_balance: a.initial_balance ?? 0,
                  } })} data-testid={`bank-edit-${a.id}`}>
                    <Edit className="h-3 w-3 mr-1" /> Editar
                  </Button>
                  {!a.is_default && (
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(a)} data-testid={`bank-delete-${a.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Nova/Editar Conta */}
      <Dialog open={dlg.open} onOpenChange={(open) => setDlg({ ...dlg, open })}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto bank-accounts-tab">
          <DialogHeader>
            <DialogTitle className="font-bold">{dlg.data.id ? "Editar" : "Nova"} Conta Bancária</DialogTitle>
            <DialogDescription>Cadastre a conta que receberá entradas ou de onde saem pagamentos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold">Nome/Apelido da Conta *</label>
              <Input value={dlg.data.name || ""} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} placeholder="Ex: Conta Corrente Bradesco" data-testid="bank-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold">Tipo</label>
                <Select value={dlg.data.account_type || "corrente"} onValueChange={(v) => setDlg({ ...dlg, data: { ...dlg.data, account_type: v } })}>
                  <SelectTrigger data-testid="bank-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrente">Conta Corrente</SelectItem>
                    <SelectItem value="poupanca">Poupança</SelectItem>
                    <SelectItem value="caixa">Caixa (Espécie)</SelectItem>
                    <SelectItem value="outra">Outra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold">Banco</label>
                <Input value={dlg.data.bank_name || ""} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, bank_name: e.target.value } })} placeholder="Ex: Bradesco" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold">Agência</label>
                <Input value={dlg.data.agency || ""} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, agency: e.target.value } })} placeholder="0000" />
              </div>
              <div>
                <label className="text-xs font-bold">Conta</label>
                <Input value={dlg.data.account_number || ""} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, account_number: e.target.value } })} placeholder="00000-0" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold">Saldo Inicial (R$)</label>
              <Input type="number" step="0.01" value={dlg.data.initial_balance ?? 0} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, initial_balance: e.target.value } })} placeholder="0.00" data-testid="bank-initial" />
              <p className="text-[11px] mt-1 font-semibold">Deixe 0 se a conta começa vazia. Editar depois recalcula o saldo automaticamente.</p>
            </div>
            <div>
              <label className="text-xs font-bold">Observações</label>
              <textarea className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={dlg.data.notes || ""} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, notes: e.target.value } })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDlg({ open: false, data: { ...emptyAccount } })}>Cancelar</Button>
              <Button className="bg-blue-600" onClick={save} data-testid="bank-save-btn">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
